/**
 * TeacherNotebookPage — past lessons + outstanding homework.
 *
 * Per Teacher Mode Build Spec §18 / §24D. Two stacked sections:
 *   1. Homework (incomplete first, then completed) — tap to toggle.
 *   2. Lesson history (most-recent first) — tap to re-open recap.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CaretLeft, CheckCircle, Notebook, GraduationCap, ChalkboardTeacher,
  ArrowRight, SpinnerGap,
} from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';

export default function TeacherNotebookPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    refresh();
  }, []);

  async function refresh() {
    try {
      const { data } = await http.get('/teacher/notebook');
      setData(data);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function toggleHomework(hw) {
    setBusyId(hw.id);
    try {
      await http.post(`/teacher/homework/${hw.id}/complete`, {
        is_completed: !hw.is_completed,
      });
      await refresh();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  }

  if (!data) {
    return (
      <div className="lf-shell flex items-center justify-center" data-testid="teacher-notebook-loading">
        <SpinnerGap size={28} className="animate-spin text-[var(--lf-primary)]" />
      </div>
    );
  }

  const homework = data.homework || [];
  const incomplete = homework.filter((h) => !h.is_completed);
  const complete = homework.filter((h) => h.is_completed);
  const runs = data.runs || [];

  return (
    <div className="lf-shell flex flex-col px-6 py-6" data-testid="teacher-notebook-page">
      <div className="flex items-center gap-2">
        <Link
          to="/home"
          className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
          data-testid="teacher-notebook-back"
        >
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
            Teacher mode
          </div>
          <h1 className="text-xl font-bold">Lesson notebook</h1>
        </div>
      </div>

      {/* Stats strip */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <Stat
          label="Lessons completed"
          value={data.completed_count || 0}
          testid="teacher-notebook-completed-count"
        />
        <Stat
          label="Homework pending"
          value={incomplete.length}
          testid="teacher-notebook-homework-pending-count"
        />
      </div>

      {error ? (
        <div className="mt-3 text-sm text-[var(--lf-destructive)]" data-testid="teacher-notebook-error">
          {error}
        </div>
      ) : null}

      {/* Homework section */}
      <h2 className="mt-7 text-sm uppercase tracking-widest text-[var(--lf-text-muted)] flex items-center gap-1.5">
        <Notebook size={14} /> Homework
      </h2>
      {homework.length === 0 ? (
        <div className="mt-2 text-sm text-[var(--lf-text-muted)]" data-testid="teacher-notebook-no-homework">
          No homework yet — start a lesson to earn one.
        </div>
      ) : (
        <div className="mt-3 space-y-2" data-testid="teacher-notebook-homework-list">
          {[...incomplete, ...complete].map((hw) => (
            <button
              key={hw.id}
              onClick={() => toggleHomework(hw)}
              disabled={busyId === hw.id}
              className={`w-full text-left rounded-2xl border p-3 transition-colors ${
                hw.is_completed
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-[var(--lf-border)] bg-[var(--lf-surface)]'
              }`}
              data-testid={`teacher-notebook-homework-${hw.id}`}
            >
              <div className="flex items-start gap-2.5">
                <div className={`mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-full ${
                  hw.is_completed
                    ? 'bg-emerald-600 text-white'
                    : 'border border-[var(--lf-border)]'
                }`}>
                  {hw.is_completed ? <CheckCircle size={14} weight="fill" /> : null}
                </div>
                <div className="flex-1">
                  <div className={`text-sm ${
                    hw.is_completed ? 'text-emerald-900 line-through' : ''
                  }`}>
                    {hw.instruction}
                  </div>
                  <div className="mt-1 text-xs text-[var(--lf-text-muted)]">
                    {hw.is_completed ? 'Done' : 'Tap when finished'}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Lesson history */}
      <h2 className="mt-8 text-sm uppercase tracking-widest text-[var(--lf-text-muted)] flex items-center gap-1.5">
        <GraduationCap size={14} /> Recent lessons
      </h2>
      {runs.length === 0 ? (
        <div className="mt-2 text-sm text-[var(--lf-text-muted)]" data-testid="teacher-notebook-no-runs">
          You haven't taken any teacher lessons yet.{' '}
          <Link to="/teacher/today" className="underline">Start today's lesson →</Link>
        </div>
      ) : (
        <div className="mt-3 space-y-2 pb-6" data-testid="teacher-notebook-runs-list">
          {runs.map((r) => (
            <motion.button
              key={r.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => {
                if (r.completed_at) navigate(`/teacher/recap/${r.id}`);
                else navigate(`/teacher/run/${r.id}`);
              }}
              className="w-full text-left rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3 hover:border-[var(--lf-primary)] transition-colors"
              data-testid={`teacher-notebook-run-${r.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <ChalkboardTeacher size={12} className="text-[var(--lf-primary)]" />
                    <span className="text-[10px] uppercase tracking-widest text-[var(--lf-text-muted)]">
                      {r.cefr_band || 'A1'} · {r.completed_at ? 'Done' : 'In progress'}
                    </span>
                  </div>
                  <div className="text-sm font-semibold mt-0.5 truncate">{r.lesson_title}</div>
                  <div className="text-xs text-[var(--lf-text-muted)] truncate">{r.objective}</div>
                </div>
                <ArrowRight size={14} className="text-[var(--lf-text-muted)] flex-shrink-0" />
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div
      className="rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3.5"
      data-testid={testid}
    >
      <div className="text-2xl font-bold text-[var(--lf-text)]">{value}</div>
      <div className="text-xs text-[var(--lf-text-muted)] mt-0.5">{label}</div>
    </div>
  );
}
